<template>
  <div class="animated fadeIn system-bans-view">
    <div class="main-container">

      <!-- HEADER & STATS CARD -->
      <b-card no-body class="border-0 shadow-sm mb-3 bg-dark text-white overflow-hidden polished-card">
        <b-card-body class="p-4">
          <div class="d-flex flex-column flex-lg-row align-items-center justify-content-between">

            <div class="d-flex align-items-center mb-3 mb-lg-0">
              <div class="bg-primary-soft rounded p-3 mr-3 shadow-sm-glow">
                <i class="fa fa-shield fa-2x text-primary"></i>
              </div>
              <div>
                <h4 class="mb-0 font-weight-bold letter-spacing-1 text-uppercase">System Bans</h4>
              </div>
            </div>

            <div class="d-flex align-items-center flex-wrap justify-content-center">
              <!-- Stats Counters -->
              <div class="d-flex border-right border-dark-accent pr-4 mr-4 d-none d-sm-flex">
                <div class="text-center mr-4">
                  <div class="text-muted smaller font-weight-bold uppercase mb-1">IP Blocks</div>
                  <div class="h3 mb-0 font-weight-bold text-info">{{ ipBans ? ipBans.length : 0 }}</div>
                </div>
                <div class="text-center border-left border-dark-accent pl-4">
                  <div class="text-muted smaller font-weight-bold uppercase mb-1">MAC Blocks</div>
                  <div class="h3 mb-0 font-weight-bold text-warning">{{ macBans ? macBans.length : 0 }}</div>
                </div>
              </div>

              <!-- Toolbar: Search & Add -->
              <div class="d-flex align-items-center">
                <div class="search-box-wrapper mr-3 shadow-sm">
                  <b-input-group size="sm" style="width: 240px;">
                    <b-input-group-prepend class="bg-darker border-0 text-muted px-3 d-flex align-items-center">
                      <i class="fa fa-search"></i>
                    </b-input-group-prepend>
                    <b-form-input
                      v-model="quickFilter"
                      placeholder="Filter database..."
                      class="bg-darker border-0 text-white font-weight-600 py-3"
                      @input="onQuickFilterChanged"
                    />
                  </b-input-group>
                </div>

                <b-button
                  v-if="(tabIndex === 0 && hasPermission(BanIP)) || (tabIndex === 1 && hasPermission(UserBanMac))"
                  variant="danger"
                  size="sm"
                  @click="tabIndex === 0 ? showAddIPModal() : showAddMACModal()"
                  class="px-4 py-2 font-weight-bold shadow-sm rounded-sm btn-action"
                >
                  <i class="fa fa-plus mr-2"></i> ADD BAN
                </b-button>
              </div>
            </div>

          </div>
        </b-card-body>
      </b-card>

      <!-- DATA GRID CARD -->
      <b-card no-body class="border-0 shadow-sm bg-dark text-white overflow-hidden polished-card">
        <b-tabs v-model="tabIndex" card nav-class="px-4 bg-darker-accent border-0 pt-2 custom-ban-tabs">

          <!-- IP TAB -->
          <b-tab active v-if="hasPermission(SystemBanView) || hasPermission(BanIP)">
            <template #title>
              <div class="d-flex align-items-center py-1 px-2">
                <i class="fa fa-globe mr-2"></i> <span>IP ADDRESSES</span>
              </div>
            </template>
            <div class="p-0">
              <ag-grid-vue
                style="width: 100%; height: calc(100vh - 380px);"
                class="ag-theme-balham-dark integrated-grid"
                :gridOptions="ipGridOptions"
                :rowData="ipBans"
                :context="context"
                @grid-ready="onGridReadyIP"
              />
            </div>
          </b-tab>

          <!-- MAC TAB -->
          <b-tab v-if="hasPermission(SystemBanView) || hasPermission(UserBanMac)">
            <template #title>
              <div class="d-flex align-items-center py-1 px-2">
                <i class="fa fa-laptop mr-2"></i> <span>MAC DEVICES</span>
              </div>
            </template>
            <div class="p-0">
              <ag-grid-vue
                style="width: 100%; height: calc(100vh - 380px);"
                class="ag-theme-balham-dark integrated-grid"
                :gridOptions="macGridOptions"
                :rowData="macBans"
                :context="context"
                @grid-ready="onGridReadyMAC"
              />
            </div>
          </b-tab>
        </b-tabs>
      </b-card>

      <!-- COMPACT SHARED MODAL -->
      <b-modal v-model="showSharedModal" size="md" scrollable hide-footer
               header-bg-variant="dark" header-text-variant="white"
               body-bg-variant="darker" body-class="p-0"
               content-class="border-0 shadow-lg bg-dark rounded-lg">
        
        <template #modal-title>
          <div class="pr-5" style="word-break: break-all;">
            <span class="text-muted smaller font-weight-bold text-uppercase d-block mb-1" style="letter-spacing: 1px;">Shared Identifier</span>
            <span class="text-info font-weight-bold" style="font-size: 0.95rem;">{{ modalIdentifier }}</span>
          </div>
        </template>

        <div v-if="sharedIPs && sharedIPs.length" class="compact-shared-list">
            <div class="px-4 py-2 bg-dark text-muted smaller font-weight-bold uppercase border-bottom border-dark-accent d-flex justify-content-between align-items-center sticky-top">
                <span>Affected Account</span>
                <b-badge variant="dark" class="border border-secondary">{{ sharedIPs.length }} Found</b-badge>
            </div>
            <div class="list-group list-group-flush">
                <div v-for="user in sharedIPs" :key="user.username" class="list-group-item bg-transparent text-white border-dark-accent py-2 px-4 d-flex justify-content-between align-items-center hover-dark-row">
                    <div class="d-flex align-items-center">
                        <div class="status-dot mr-3 shadow-glow" :class="user.banned === 'P' ? 'bg-danger' : 'bg-success'"></div>
                        <div class="d-flex flex-column">
                            <span class="font-weight-bold h6 mb-0 text-white-90">{{ user.username }}</span>
                            <small class="text-muted smaller">Reg: {{ formatDateDisplay(user.reg_date) }}</small>
                        </div>
                    </div>
                    <div class="text-right d-flex flex-column align-items-end">
                        <b-badge v-if="user.banned === 'P'" variant="danger" pill class="mb-1 px-2">BANNED</b-badge>
                        <b-badge v-else variant="success" pill class="mb-1 px-2">ACTIVE</b-badge>
                        <small class="text-muted smaller font-italic">{{ formatDateDisplay(user.date) }}</small>
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="text-center py-5">
          <i class="fa fa-search fa-3x text-muted mb-3 opacity-20"></i>
          <h5 class="text-muted">No associated records detected.</h5>
        </div>

        <div class="p-3 text-center bg-dark border-top border-dark-accent">
          <b-button variant="outline-secondary" size="sm" @click="showSharedModal = false" class="px-5 font-weight-bold letter-spacing-1">DISMISS</b-button>
        </div>
      </b-modal>

      <!-- ADD MODALS -->
      <b-modal title="Global IP Block" v-model="addIPModal" @ok="addIP" header-bg-variant="danger" header-text-variant="white" body-bg-variant="dark" centered>
        <div class="text-center py-3">
          <i class="fa fa-globe fa-3x text-danger mb-3 opacity-50"></i>
          <p class="text-muted mb-3">Enter the IP address to blacklist across all servers.</p>
          <b-form-input v-model="newIP" placeholder="0.0.0.0" size="lg" class="text-center bg-darker border-dark text-white font-weight-bold polished-input" autofocus />
        </div>
      </b-modal>

      <b-modal title="Global MAC Block" v-model="addMACModal" @ok="addMAC" header-bg-variant="danger" header-text-variant="white" body-bg-variant="dark" centered>
        <div class="text-center py-3">
          <i class="fa fa-laptop fa-3x text-danger mb-3 opacity-50"></i>
          <p class="text-muted mb-3">Enter the hardware MAC to blacklist globally.</p>
          <b-form-input v-model="newMAC" placeholder="XX:XX:XX:XX:XX:XX" size="lg" class="text-center bg-darker border-dark text-white font-weight-bold polished-input" autofocus />
        </div>
      </b-modal>

    </div>
  </div>
</template>

<script>
import {AgGridVue} from 'ag-grid-vue'
import {DefaultGridOptions} from "@/default_table_settings"
import {applyGridPersistence} from "@/grid_persistence"
import iziToast from 'izitoast'
import moment from 'moment'
import { mapGetters } from 'vuex'

export default {
  name: 'SystemBans',
  components: {
    'ag-grid-vue': AgGridVue
  },
  data() {
      return {
          tabIndex: 0,
          ipGridOptions: null,
          macGridOptions: null,
          context: { componentParent: this },
          addIPModal: false,
          addMACModal: false,
          newIP: '',
          newMAC: '',
          modalTitle: '',
          modalIdentifier: '',
          showSharedModal: false,
          quickFilter: '',
          gridApiIP: null,
          gridApiMAC: null
      }
  },
  computed: mapGetters({
      ipBans: 'systemBansIPs',
      macBans: 'systemBansMACs',
      error: 'systemBanError',
      sharedIPs: 'sharedIPs'
  }),
  created() {
      const commonColDef = {
          sortable: true, resizable: true, filter: true,
          cellStyle: {'font-size': '16px', 'display': 'flex', 'align-items': 'center'}
      }

      // IP GRID
      const ipColDefs = [
          { 
              headerName: 'IP ADDRESS', field: 'ip', 
              cellStyle: {cursor: 'pointer', color: '#20a8d8', 'font-weight': '700', 'font-size': '16px', 'display': 'flex', 'align-items': 'center'},
              width: 300, ...commonColDef
          },
          { 
              headerName: 'DATE REGISTERED', field: 'date', 
              cellRenderer: (params) => params.value ? moment(params.value).format('YYYY-MM-DD HH:mm:ss') : '',
              width: 250, ...commonColDef
          },
          { headerName: '', field: 'spacer', flex: 1, suppressMenu: true, sortable: false, resizable: true }
      ]

      if (this.hasPermission(this.BanIP)) {
          ipColDefs.push({
              headerName: '', width: 70, suppressMenu: true, sortable: false, pinned: 'right',
              cellRenderer: () => `<div class="w-100 text-center"><i class="fa fa-trash text-danger action-icon" style="cursor:pointer; font-size: 18px;" title="Remove Block"></i></div>`,
              cellStyle: {'display': 'flex', 'align-items': 'center', 'justify-content': 'center', 'padding': '0'},
              onCellClicked: (params) => this.confirmUnban(params.data.ip, 'ip')
          })
      }

      this.ipGridOptions = Object.assign({}, DefaultGridOptions, {
          columnDefs: ipColDefs,
          onCellClicked: (params) => {
              if (params.colDef.field === 'ip') this.getShared(params.value, 'ip')
          },
          rowHeight: 52, headerHeight: 48
      })
      applyGridPersistence(this.ipGridOptions, 'ip-bans-grid')
      
      // MAC GRID
      const macColDefs = [
          { 
              headerName: 'HARDWARE MAC', field: 'mac', 
              cellStyle: {cursor: 'pointer', color: '#20a8d8', 'font-weight': '700', 'font-size': '16px', 'display': 'flex', 'align-items': 'center'},
              width: 320, ...commonColDef
          },
          { 
              headerName: 'SOURCE USER', field: 'user', 
              cellStyle: {'font-weight': '600', 'font-size': '15px', 'color': '#a4b7c1', 'display': 'flex', 'align-items': 'center'},
              width: 220, ...commonColDef,
              cellRenderer: params => params.value ? params.value : '<span class="text-muted font-italic">System / Manual</span>'
          },
          { 
              headerName: 'DATE REGISTERED', field: 'date', 
              cellRenderer: (params) => params.value ? moment(params.value).format('YYYY-MM-DD HH:mm:ss') : '',
              width: 250, ...commonColDef
          },
          { headerName: '', field: 'spacer', flex: 1, suppressMenu: true, sortable: false, resizable: true }
      ]

      if (this.hasPermission(this.UserBanMac)) {
          macColDefs.push({
              headerName: '', width: 70, suppressMenu: true, sortable: false, pinned: 'right',
              cellRenderer: () => `<div class="w-100 text-center"><i class="fa fa-trash text-danger action-icon" style="cursor:pointer; font-size: 18px;" title="Remove Block"></i></div>`,
              cellStyle: {'display': 'flex', 'align-items': 'center', 'justify-content': 'center', 'padding': '0'},
              onCellClicked: (params) => this.confirmUnban(params.data.mac, 'mac')
          })
      }
      
      this.macGridOptions = Object.assign({}, DefaultGridOptions, {
          columnDefs: macColDefs,
          onCellClicked: (params) => {
              if (params.colDef.field === 'mac') this.getShared(params.value, 'mac')
          },
          rowHeight: 52, headerHeight: 48
      })
      applyGridPersistence(this.macGridOptions, 'mac-bans-grid')
      
      this.refresh()
  },
  watch: {
      error: function (val) {
          if (val && val.error === 1) {
              iziToast.success({title: 'Success', message: 'Registry updated.', position: 'topRight'})
              this.refresh()
          }
      },
      sharedIPs: function(val) {
          if (val && val.length > 0) {
              this.showSharedModal = true
          } else if (val && val.length === 0) {
              iziToast.info({title: 'No Results', message: 'No associated accounts found.', position: 'topRight'})
          }
      }
  },
  methods: {
      refresh() { this.emit('getSystemBans', {}) },
      onGridReadyIP(params) { this.gridApiIP = params.api; },
      onGridReadyMAC(params) { this.gridApiMAC = params.api; },
      onQuickFilterChanged() {
          if (this.gridApiIP) this.gridApiIP.setQuickFilter(this.quickFilter)
          if (this.gridApiMAC) this.gridApiMAC.setQuickFilter(this.quickFilter)
      },
      showAddIPModal() { this.newIP = ''; this.addIPModal = true },
      showAddMACModal() { this.newMAC = ''; this.addMACModal = true },
      addIP() { if (this.newIP) this.emit('banIP', { ip: this.newIP, username: '' }) },
      addMAC() { if (this.newMAC) this.emit('banMAC', { mac: this.newMAC, username: '' }) },
      getShared(val, type) {
          this.modalIdentifier = val
          this.modalTitle = 'Shared Identifier'
          if (type === 'ip') {
              this.emit('getSharedIPs', {'ip': val, 'server': 'gold'})
          } else {
              this.emit('getSharedMACs', {'mac': val, 'server': 'gold'})
          }
      },
      unbanIP(ip) { this.emit('unbanIP', ip) },
      unbanMAC(mac) { this.emit('unbanMacDirectly', mac) },
      formatMinutes (m) { return m > 60 ? `${Math.floor(m/60)}h ${m%60}m` : `${m}m` },
      formatPrice (v) { return v ? v.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') : '0' },
      confirmUnban(val, type) {
          iziToast.question({
              timeout: 20000, overlay: true, displayMode: 'once', id: 'question', zindex: 99999,
              title: 'Confirm Unban', message: `Remove global block for this ${type.toUpperCase()}?`,
              position: 'center',
              buttons: [
                  ['<button><b>YES</b></button>', (instance, toast) => {
                      instance.hide({ transitionOut: 'fadeOut' }, toast, 'button');
                      if (type === 'ip') this.unbanIP(val); else this.unbanMAC(val);
                  }, true],
                  ['<button>NO</button>', (instance, toast) => instance.hide({transitionOut:'fadeOut'},toast,'button')]
              ]
          });
      }
  }
}
</script>

<style scoped>
.bg-darker { background-color: #1a1d21 !important; }
.bg-darker-accent { background-color: #23282c !important; }
.bg-primary-soft { background-color: rgba(32, 168, 216, 0.1); }
.border-dark-light { border-color: rgba(255,255,255,0.05) !important; }
.border-dark-accent { border-color: #3e444a !important; }
.text-light-gray { color: #cfd8dc !important; }
.text-white-90 { color: rgba(255,255,255,0.9); }

/* Dashboard Tabs Polish */
/deep/ .custom-ban-tabs.nav-tabs { border-bottom: none; }
/deep/ .custom-ban-tabs .nav-link {
  border: none; border-bottom: 3px solid transparent; color: #73818f;
  font-weight: 700; text-transform: uppercase; font-size: 0.85rem; letter-spacing: 1px;
  padding: 1.2rem 2.5rem; transition: all 0.25s ease;
}
/deep/ .custom-ban-tabs .nav-link.active {
  background: transparent !important; color: #20a8d8 !important; border-bottom-color: #20a8d8 !important;
}
/deep/ .custom-ban-tabs .nav-link:hover:not(.active) {
  color: #fff; border-bottom-color: rgba(255,255,255,0.1);
}

/* Polished Grid */
/deep/ .integrated-grid { border: none; }
/deep/ .ag-header-cell-label { text-transform: uppercase; font-weight: 700; letter-spacing: 0.5px; color: #a4b7c1; }
.action-icon:hover { transform: scale(1.2); transition: transform 0.2s; }

/* Modal & Status UI */
.hover-dark-row:hover { background-color: #2a2e33 !important; }
.status-dot { width: 10px; height: 10px; border-radius: 50%; }
.shadow-glow { box-shadow: 0 0 10px rgba(32, 168, 216, 0.2); }
.shadow-sm-glow { box-shadow: 0 0 15px rgba(32, 168, 216, 0.15); }
.smaller { font-size: 0.75rem; }

.polished-input:focus { background-color: #23282c !important; color: #fff !important; }
.btn-action { transition: all 0.2s; letter-spacing: 0.5px; }
.btn-action:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(248, 108, 107, 0.3) !important; }
</style>
